import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../../store/store';
import {
  type CoachListItem,
  type CoachSortBy,
  type SortDir,
  useListCoachesQuery,
  useUpdateCoachSettingsMutation,
} from './api/coachesApi';
import { useStartImpersonationMutation } from './api/impersonationApi';
import { startAdminImpersonationSession } from './utils/adminImpersonation';

export default function CoachesListPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const per = 25;

  // Sorting state - default to first_login descending (newest first)
  const [sortBy, setSortBy] = useState<CoachSortBy>('first_login');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [excludeAdmins, setExcludeAdmins] = useState(false);
  const [driveFilter, setDriveFilter] = useState<'all' | 'connected' | 'not_connected'>('all');

  const currentToken = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'error',
  });

  const { data, isLoading, isFetching, error, refetch } = useListCoachesQuery({
    q: searchTerm || undefined,
    page,
    per,
    sort_by: sortBy,
    sort_dir: sortDir,
    include_disabled: includeDisabled,
    ...(excludeAdmins ? { exclude_admins: true } : {}),
    ...(driveFilter === 'connected' ? { drive_connected: true } : {}),
    ...(driveFilter === 'not_connected' ? { drive_connected: false } : {}),
  });

  const handleDriveFilterChange = (event: SelectChangeEvent) => {
    setDriveFilter(event.target.value as 'all' | 'connected' | 'not_connected');
    setPage(1);
  };

  const handleSort = (column: CoachSortBy) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to desc for counts, asc for text
      setSortBy(column);
      setSortDir(['name', 'email'].includes(column) ? 'asc' : 'desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const [updateSettings] = useUpdateCoachSettingsMutation();
  const [startImpersonationApi, { isLoading: isImpersonating }] = useStartImpersonationMutation();

  const coaches = data?.coaches ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / per));

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleCopyEnabledEmails = async () => {
    const enabledCoaches = coaches.filter((coach) => !coach.disabled_at);
    const excludedCount = coaches.length - enabledCoaches.length;
    if (enabledCoaches.length === 0) {
      setSnackbar({
        open: true,
        message: 'No enabled coach emails on this page to copy.',
        severity: 'warning',
      });
      return;
    }

    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(enabledCoaches.map((coach) => coach.email).join('\n'));
      const message =
        excludedCount > 0
          ? `Copied ${enabledCoaches.length} of ${coaches.length} emails — ${excludedCount} disabled ${excludedCount === 1 ? 'coach' : 'coaches'} excluded.`
          : `Copied ${enabledCoaches.length} ${enabledCoaches.length === 1 ? 'email' : 'emails'}.`;
      setSnackbar({ open: true, message, severity: 'success' });
    } catch {
      setSnackbar({
        open: true,
        message: 'Could not copy coach emails. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleToggleAutoSummary = async (coach: CoachListItem) => {
    const newValue = !coach.auto_summary_enabled;
    const action = newValue ? 'enable' : 'disable';
    if (!window.confirm(`${action} auto-summary emails for ${coach.name}?`)) return;

    try {
      await updateSettings({
        coachId: coach.id,
        settings: { auto_summary_enabled: newValue },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update settings:', err);
    }
  };

  const handleToggleReflectionEmail = async (coach: CoachListItem) => {
    const newValue = !(coach.auto_reflection_email_enabled ?? true);
    try {
      await updateSettings({
        coachId: coach.id,
        settings: { auto_reflection_email_enabled: newValue },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update reflection email setting:', err);
    }
  };

  const handleRowClick = (coachId: string) => {
    navigate(`/admin/coaches/${coachId}`);
  };

  const handleImpersonate = async (coach: CoachListItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't navigate to coach detail

    if (!currentToken || !currentUser) {
      console.error('No current token/user available');
      return;
    }

    try {
      const result = await startImpersonationApi({ target_person_id: coach.id }).unwrap();
      startAdminImpersonationSession({
        dispatch,
        navigate,
        currentToken,
        currentUser,
        result,
      });
    } catch (err) {
      console.error('Failed to start impersonation:', err);
      const errorMessage =
        (err as any)?.data?.error_description ||
        (err as any)?.message ||
        'Failed to start impersonation. Please try again.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 1125, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Coaches
        </Typography>
        {(isLoading || isFetching) && <CircularProgress size={20} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View coaches, their members, and manage summary email settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load coaches. {(error as any)?.data?.error_description || 'Please try again.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email or name"
            size="small"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }}
            sx={{ minWidth: 250 }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
            Search
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyEnabledEmails}
            disabled={coaches.length === 0}
          >
            Copy enabled emails on this page
          </Button>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="coach-drive-filter-label">Drive</InputLabel>
            <Select
              labelId="coach-drive-filter-label"
              value={driveFilter}
              label="Drive"
              onChange={handleDriveFilterChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="connected">Connected</MenuItem>
              <MenuItem value="not_connected">Not connected</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={includeDisabled}
                onChange={(e) => {
                  setIncludeDisabled(e.target.checked);
                  setPage(1);
                }}
                size="small"
              />
            }
            label="Show disabled"
            sx={{ ml: 1 }}
          />
          <Tooltip title="When enabled, internal admin users are removed from coach stats." arrow>
            <FormControlLabel
              control={
                <Switch
                  checked={excludeAdmins}
                  onChange={(e) => {
                    setExcludeAdmins(e.target.checked);
                    setPage(1);
                  }}
                  size="small"
                />
              }
              label="Exclude admins"
            />
          </Tooltip>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Page {page} of {totalPages} • {total} coaches
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'name' || sortBy === 'email'}
                    direction={sortBy === 'name' || sortBy === 'email' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Coach
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'members'}
                    direction={sortBy === 'members' ? sortDir : 'desc'}
                    onClick={() => handleSort('members')}
                  >
                    Members
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'sessions'}
                    direction={sortBy === 'sessions' ? sortDir : 'desc'}
                    onClick={() => handleSort('sessions')}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'artifacts'}
                    direction={sortBy === 'artifacts' ? sortDir : 'desc'}
                    onClick={() => handleSort('artifacts')}
                  >
                    Artifacts
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'first_login'}
                    direction={sortBy === 'first_login' ? sortDir : 'desc'}
                    onClick={() => handleSort('first_login')}
                  >
                    First Login
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'last_active'}
                    direction={sortBy === 'last_active' ? sortDir : 'desc'}
                    onClick={() => handleSort('last_active')}
                  >
                    Last Active
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Auto-Summary emails</TableCell>
                <TableCell align="center">Reflection emails</TableCell>
                <TableCell align="center">Drive</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coaches.map((coach) => (
                <TableRow
                  key={coach.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    opacity: coach.disabled_at ? 0.6 : 1,
                    backgroundColor: coach.disabled_at ? 'action.disabledBackground' : 'inherit',
                  }}
                  onClick={() => handleRowClick(coach.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle2">{coach.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {coach.email}
                        </Typography>
                      </Box>
                      {coach.disabled_at && (
                        <Chip
                          icon={<BlockIcon />}
                          label="Disabled"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{coach.member_count}</TableCell>
                  <TableCell align="right">{coach.session_count}</TableCell>
                  <TableCell align="right">{coach.artifact_count}</TableCell>
                  <TableCell>
                    {coach.first_login_at
                      ? formatDistanceToNow(new Date(coach.first_login_at), { addSuffix: true })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {coach.last_active_at
                      ? formatDistanceToNow(new Date(coach.last_active_at), { addSuffix: true })
                      : '-'}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={coach.auto_summary_enabled}
                      onChange={() => handleToggleAutoSummary(coach)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={coach.auto_reflection_email_enabled ?? true}
                      onChange={() => handleToggleReflectionEmail(coach)}
                      size="small"
                      inputProps={{ 'aria-label': `Reflection emails for ${coach.name}` }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {coach.drive_connected ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Connected"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<CloudOffIcon />}
                        label="Not connected"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View as this coach (read-only)">
                      <IconButton
                        size="small"
                        onClick={(e) => handleImpersonate(coach, e)}
                        disabled={isImpersonating}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {coaches.length === 0 && !isLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No coaches found.</Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Showing {coaches.length} of {total}
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
